import React, {FC, useId, useState} from "react";
import {Button, Input, InputNumber, Select, Space, Switch, Tooltip, Typography} from "antd";
import {DeleteOutlined, DownOutlined, PlusOutlined, UpOutlined} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import {
    getTemplateFieldKind,
    isTemplateQrCodeField,
    normalizeTemplateTextFieldStyle,
    TEMPLATE_PARTICIPANT_FIELD_OPTIONS,
    TEMPLATE_TEXT_FIELD_FONT_FAMILIES,
    TEMPLATE_TEXT_FIELD_FONT_WEIGHTS,
    TemplateTextFieldDefinition,
    TemplateTextFieldStyle,
    TemplateTextFieldSource
} from "./templateTextFields";
import {DeletePopconfirm} from "../../Components/DeleteConfirmation";

type TemplateTextFieldListLabels = {
    add: string;
    expand: string;
    collapse: string;
    fieldLabel: string;
    backgroundColor: string;
    backgroundOpacity: string;
    borderColor: string;
    borderOpacity: string;
    borderRadius: string;
    borderWidth: string;
    color: string;
    fontFamily: string;
    fontSize: string;
    fontStyle: string;
    fontStyleItalic: string;
    fontStyleNormal: string;
    fontWeight: string;
    participantField: string;
    positionX: string;
    positionY: string;
    remove: string;
    shadow: string;
    source: string;
    sourceParticipant: string;
    sourceStatic: string;
    staticText: string;
    title: string;
    type: string;
    typeQr: string;
    typeText: string;
    size: string;
};

type TemplateTextFieldListProps = {
    fields: TemplateTextFieldDefinition[];
    labels: TemplateTextFieldListLabels;
    logicalHeight: number;
    logicalWidth: number;
    showFieldType?: boolean;
    onAdd: () => void;
    onRemove: (id: string) => void;
    onUpdate: (id: string, changes: Partial<TemplateTextFieldDefinition>) => void;
};

const opacityPercent = (value: number) => Math.round(value * 100);

const TemplateColorInput: FC<{
    label: string;
    onChange: (value: string) => void;
    value: string;
}> = ({label, onChange, value}) => (
    <Space direction="vertical" size={2}>
        <Typography.Text type="secondary">{label}</Typography.Text>
        <Input
            type="color"
            value={value}
            onChange={event => onChange(event.target.value)}
            style={{height: 32, minWidth: 48, padding: 3}}
        />
    </Space>
);

export const TemplateTextFieldList: FC<TemplateTextFieldListProps> = ({
    fields,
    labels,
    logicalHeight,
    logicalWidth,
    showFieldType = false,
    onAdd,
    onRemove,
    onUpdate
}) => {
    const {t} = useTranslation();
    const listId = useId();
    const [expandedFields, setExpandedFields] = useState<string[]>([]);
    const updateStyle = (
        field: TemplateTextFieldDefinition,
        changes: Partial<TemplateTextFieldStyle>
    ) => onUpdate(field.id, {
        style: {
            ...normalizeTemplateTextFieldStyle(field.style),
            ...changes
        }
    });

    return (
        <div className="ati-stack ati-stack--compact">
            <div style={{
                alignItems: "center",
                display: "flex",
                gap: 12,
                justifyContent: "space-between"
            }}>
                <Typography.Text strong>{labels.title}</Typography.Text>
                <Button icon={<PlusOutlined/>} onClick={onAdd}>
                    {labels.add}
                </Button>
            </div>
            {fields.map((field, index) => {
                const style = normalizeTemplateTextFieldStyle(field.style);
                const isQrCode = isTemplateQrCodeField(field);
                const expanded = expandedFields.includes(field.id);
                const panelId = `${listId}-${field.id}`;
                const toggleLabel = expanded ? labels.collapse : labels.expand;

                return (
                    <div key={field.id} className="template-text-field">
                        <div className="template-text-field__header">
                            <span className="template-text-field__number">{index + 1}.</span>
                            <span className="template-text-field__label" title={field.label}>{field.label}</span>
                            <Tooltip title={toggleLabel}>
                                <Button
                                    type="text"
                                    className="template-text-field__toggle"
                                    aria-label={`${toggleLabel}: ${index + 1}. ${field.label}`}
                                    aria-expanded={expanded}
                                    aria-controls={panelId}
                                    icon={expanded ? <UpOutlined/> : <DownOutlined/>}
                                    onClick={() => setExpandedFields(previous => expanded
                                        ? previous.filter(id => id !== field.id)
                                        : [...previous, field.id])}
                                />
                            </Tooltip>
                        </div>
                        <div id={panelId} hidden={!expanded}>
                            {expanded && (
                                <div className="template-text-field__parameters">
                                    <div
                                        style={{
                                            alignItems: "end",
                                            display: "grid",
                                            gap: 10,
                                            gridTemplateColumns: "repeat(auto-fit, minmax(min(180px, 100%), 1fr))"
                                        }}
                                    >
                                        <Space direction="vertical" size={2}>
                                            <Typography.Text type="secondary">{labels.fieldLabel}</Typography.Text>
                                            <Input
                                                aria-label={`${field.label} ${labels.fieldLabel}`}
                                                value={field.label}
                                                onChange={event => onUpdate(field.id, {label: event.target.value})}
                                            />
                                        </Space>
                                        {showFieldType && (
                                            <Space direction="vertical" size={2}>
                                                <Typography.Text type="secondary">{labels.type}</Typography.Text>
                                                <Select
                                                    aria-label={`${field.label} ${labels.type}`}
                                                    disabled
                                                    value={getTemplateFieldKind(field)}
                                                    options={[isQrCode
                                                        ? {label: labels.typeQr, value: "qr"}
                                                        : {label: labels.typeText, value: "text"}]}
                                                />
                                            </Space>
                                        )}
                                        {!isQrCode && (
                                            <>
                                                <Space direction="vertical" size={2}>
                                                    <Typography.Text type="secondary">{labels.source}</Typography.Text>
                                                    <Select
                                                        value={field.source}
                                                        onChange={(source: TemplateTextFieldSource) => onUpdate(field.id, {
                                                            source,
                                                            participantField: source === "participant" ? field.participantField || "fullName" : "",
                                                            staticText: source === "static" ? field.staticText || field.label : ""
                                                        })}
                                                        options={[
                                                            {label: labels.sourceParticipant, value: "participant"},
                                                            {label: labels.sourceStatic, value: "static"}
                                                        ]}
                                                    />
                                                </Space>
                                                <Space direction="vertical" size={2}>
                                                    <Typography.Text type="secondary">
                                                        {field.source === "static" ? labels.staticText : labels.participantField}
                                                    </Typography.Text>
                                                    {field.source === "static" ? (
                                                        <Input
                                                            value={field.staticText}
                                                            onChange={event => onUpdate(field.id, {staticText: event.target.value})}
                                                        />
                                                    ) : (
                                                        <Select
                                                            value={field.participantField}
                                                            onChange={participantField => onUpdate(field.id, {participantField})}
                                                            options={TEMPLATE_PARTICIPANT_FIELD_OPTIONS.map(option => ({
                                                                label: option.label,
                                                                value: option.value
                                                            }))}
                                                        />
                                                    )}
                                                </Space>
                                            </>
                                        )}
                                        <DeletePopconfirm
                                            itemType={t("delete-target-template-field", {defaultValue: "template field"})}
                                            itemName={field.label}
                                            disabled={isQrCode}
                                            onConfirm={() => {
                                                setExpandedFields(previous => previous.filter(id => id !== field.id));
                                                onRemove(field.id);
                                            }}
                                        >
                                            <Button
                                                danger
                                                type="text"
                                                aria-label={labels.remove}
                                                disabled={isQrCode}
                                                icon={<DeleteOutlined/>}
                                                style={{justifySelf: "end"}}
                                            />
                                        </DeletePopconfirm>
                                    </div>
                                    <div
                                        style={{
                                            alignItems: "end",
                                            display: "grid",
                                            gap: 10,
                                            gridTemplateColumns: "repeat(auto-fit, minmax(112px, 1fr))"
                                        }}
                                    >
                                        <Space direction="vertical" size={2}>
                                            <Typography.Text type="secondary">{labels.positionX}</Typography.Text>
                                            <InputNumber
                                                aria-label={`${field.label} ${labels.positionX}`}
                                                min={0}
                                                max={logicalWidth}
                                                precision={0}
                                                value={field.x}
                                                onChange={value => onUpdate(field.id, {x: Number(value ?? 0)})}
                                                style={{width: "100%"}}
                                            />
                                        </Space>
                                        <Space direction="vertical" size={2}>
                                            <Typography.Text type="secondary">{labels.positionY}</Typography.Text>
                                            <InputNumber
                                                aria-label={`${field.label} ${labels.positionY}`}
                                                min={0}
                                                max={logicalHeight}
                                                precision={0}
                                                value={field.y}
                                                onChange={value => onUpdate(field.id, {y: Number(value ?? 0)})}
                                                style={{width: "100%"}}
                                            />
                                        </Space>
                                        {isQrCode && (
                                            <Space direction="vertical" size={2}>
                                                <Typography.Text type="secondary">{labels.size}</Typography.Text>
                                                <InputNumber
                                                    aria-label={`${field.label} ${labels.size}`}
                                                    min={32}
                                                    max={240}
                                                    precision={0}
                                                    value={field.size}
                                                    onChange={value => onUpdate(field.id, {size: Number(value ?? 64)})}
                                                    style={{width: "100%"}}
                                                />
                                            </Space>
                                        )}
                                        {!isQrCode && (
                                            <TemplateColorInput
                                                label={labels.color}
                                                value={style.color}
                                                onChange={color => updateStyle(field, {color})}
                                            />
                                        )}
                                        <TemplateColorInput
                                            label={labels.backgroundColor}
                                            value={style.backgroundColor}
                                            onChange={backgroundColor => updateStyle(field, {backgroundColor})}
                                        />
                                        <Space direction="vertical" size={2}>
                                            <Typography.Text type="secondary">{labels.backgroundOpacity}</Typography.Text>
                                            <InputNumber
                                                min={0}
                                                max={100}
                                                precision={0}
                                                value={opacityPercent(style.backgroundOpacity)}
                                                onChange={value => updateStyle(field, {
                                                    backgroundOpacity: Number(value ?? 0) / 100
                                                })}
                                                style={{width: "100%"}}
                                            />
                                        </Space>
                                        <TemplateColorInput
                                            label={labels.borderColor}
                                            value={style.borderColor}
                                            onChange={borderColor => updateStyle(field, {borderColor})}
                                        />
                                        <Space direction="vertical" size={2}>
                                            <Typography.Text type="secondary">{labels.borderOpacity}</Typography.Text>
                                            <InputNumber
                                                min={0}
                                                max={100}
                                                precision={0}
                                                value={opacityPercent(style.borderOpacity)}
                                                onChange={value => updateStyle(field, {
                                                    borderOpacity: Number(value ?? 0) / 100
                                                })}
                                                style={{width: "100%"}}
                                            />
                                        </Space>
                                        <Space direction="vertical" size={2}>
                                            <Typography.Text type="secondary">{labels.borderWidth}</Typography.Text>
                                            <InputNumber
                                                min={0}
                                                max={12}
                                                precision={0}
                                                value={style.borderWidth}
                                                onChange={value => updateStyle(field, {borderWidth: Number(value ?? 0)})}
                                                style={{width: "100%"}}
                                            />
                                        </Space>
                                        <Space direction="vertical" size={2}>
                                            <Typography.Text type="secondary">{labels.borderRadius}</Typography.Text>
                                            <InputNumber
                                                min={0}
                                                max={48}
                                                precision={0}
                                                value={style.borderRadius}
                                                onChange={value => updateStyle(field, {borderRadius: Number(value ?? 0)})}
                                                style={{width: "100%"}}
                                            />
                                        </Space>
                                        {!isQrCode && (
                                            <>
                                                <Space direction="vertical" size={2}>
                                                    <Typography.Text type="secondary">{labels.fontFamily}</Typography.Text>
                                                    <Select
                                                        value={style.fontFamily}
                                                        onChange={fontFamily => updateStyle(field, {fontFamily})}
                                                        options={TEMPLATE_TEXT_FIELD_FONT_FAMILIES.map(fontFamily => ({
                                                            label: fontFamily,
                                                            value: fontFamily
                                                        }))}
                                                    />
                                                </Space>
                                                <Space direction="vertical" size={2}>
                                                    <Typography.Text type="secondary">{labels.fontSize}</Typography.Text>
                                                    <InputNumber
                                                        min={8}
                                                        max={96}
                                                        precision={0}
                                                        value={style.fontSize}
                                                        onChange={value => updateStyle(field, {fontSize: Number(value ?? 8)})}
                                                        style={{width: "100%"}}
                                                    />
                                                </Space>
                                                <Space direction="vertical" size={2}>
                                                    <Typography.Text type="secondary">{labels.fontWeight}</Typography.Text>
                                                    <Select
                                                        value={style.fontWeight}
                                                        onChange={fontWeight => updateStyle(field, {fontWeight})}
                                                        options={TEMPLATE_TEXT_FIELD_FONT_WEIGHTS.map(option => ({
                                                            label: option.label,
                                                            value: option.value
                                                        }))}
                                                    />
                                                </Space>
                                                <Space direction="vertical" size={2}>
                                                    <Typography.Text type="secondary">{labels.fontStyle}</Typography.Text>
                                                    <Select
                                                        value={style.fontStyle}
                                                        onChange={fontStyle => updateStyle(field, {fontStyle})}
                                                        options={[
                                                            {label: labels.fontStyleNormal, value: "normal"},
                                                            {label: labels.fontStyleItalic, value: "italic"}
                                                        ]}
                                                    />
                                                </Space>
                                            </>
                                        )}
                                        <Space direction="vertical" size={2}>
                                            <Typography.Text type="secondary">{labels.shadow}</Typography.Text>
                                            <div style={{alignItems: "center", display: "flex", height: 32}}>
                                                <Switch
                                                    aria-label={`${field.label} ${labels.shadow}`}
                                                    checked={style.shadowEnabled}
                                                    onChange={shadowEnabled => updateStyle(field, {shadowEnabled})}
                                                />
                                            </div>
                                        </Space>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default TemplateTextFieldList;
